import { memo, useContext } from 'react';
import { HStack, VStack } from '@library';
import type { ProductWithFixedRelatedProducts } from '../../util/processRelatedProducts';
import type { ProductItemOrVariant, ProductVariant } from '../../types';
import useTabletView from '../../../core/hooks/dom/useTabletView';
import { AppContext } from '../../../core/context';
import styles from './ProductDetails.module.scss';
import ProductDetailsMedia from './ProductDetailsMedia';
import ProductDetailsSummary from './ProductDetailsSummary';
import ProductDetailsPricing from './ProductDetailsPricing';
import ProductVariants from './ProductVariants';
import ProductHeader from './ProductHeader';

const MainSection = ({
    product,
    hasVariants,
    selectedSku,
    hideEmptyContent,
}: {
    product: ProductWithFixedRelatedProducts;
    hasVariants: boolean;
    selectedSku?: string;
    hideEmptyContent?: boolean;
}) => {
    const isTabletView = useTabletView();

    const { sku, name, isNew, onSale, media, videos, siblings, variants } =
        product;

    const productSiblings = [product, ...(siblings ?? [])].sort((a, b) =>
        a.id.localeCompare(b.id)
    );

    const detailsSku = hasVariants ? selectedSku : sku;

    const showMedia = !hideEmptyContent || media.length > 0;
    const showSelectors = productSiblings.length > 1 || variants.length > 0;

    return !isTabletView ? (
        <HStack gap="large">
            {showMedia && (
                <ProductDetailsMedia
                    title={name}
                    isNew={isNew}
                    onSale={onSale}
                    media={media}
                    videos={videos}
                />
            )}
            <VStack>
                <ProductDetailsSummary
                    product={product}
                    selectedSKU={detailsSku}
                />
                {showSelectors && (
                    <ProductVariants
                        hasSiblings={productSiblings.length > 0}
                        productSiblings={productSiblings}
                        variants={variants}
                    />
                )}
            </VStack>
        </HStack>
    ) : (
        <VStack>
            <ProductDetailsSummary product={product} selectedSKU={detailsSku} />
            {showMedia && (
                <ProductDetailsMedia
                    title={name}
                    isNew={isNew}
                    onSale={onSale}
                    media={media}
                    videos={videos}
                />
            )}
            {showSelectors && (
                <ProductVariants
                    hasSiblings={productSiblings.length > 0}
                    productSiblings={productSiblings}
                    variants={variants}
                />
            )}
        </VStack>
    );
};

const ProductSummary = ({
    product,
    hasVariants,
    selectedSku,
    selectedVariant,
    replacementProducts,
    hideEmptyContent,
}: {
    product: ProductWithFixedRelatedProducts;
    hasVariants: boolean;
    selectedSku?: string;
    selectedVariant?: ProductVariant;
    replacementProducts: ProductItemOrVariant[];
    hideEmptyContent?: boolean;
}) => {
    const { sku, onSale } = product;
    const { isInMobileView } = useContext(AppContext);

    return (
        <div className={styles.productDetailsSummary}>
            <HStack gap="large" justify="spaceBetween" wrap>
                <div className={styles.mainSection}>
                    {isInMobileView && (
                        <div className={styles.header}>
                            <ProductHeader
                                selectedSku={selectedSku}
                                product={product}
                            />
                        </div>
                    )}
                    <MainSection
                        hideEmptyContent={hideEmptyContent}
                        product={product}
                        hasVariants={hasVariants}
                        selectedSku={selectedSku}
                    />
                </div>
                <div
                    className={styles.productDetailsPricing}
                    data-test-class="productDetailsPricing"
                >
                    <ProductDetailsPricing
                        hasVariants={hasVariants}
                        sku={sku}
                        variant={selectedVariant}
                        product={product}
                        onSale={onSale}
                        replacementProducts={replacementProducts}
                    />
                </div>
            </HStack>
        </div>
    );
};

export default memo(ProductSummary);
